// Dental age estimation — pre→post educational-intervention KAP instrument.
// Drafted from the KS06 NotebookLM dossier (8 selected forensic-odontology sources;
// Sep 2026), then edited by the investigators. STARTER DRAFT pending expert validation.
//
// Edits made to the generated draft (keep this list until the expert panel signs off):
//  • KQ10 rewritten. The generated item keyed True on "Indian courts routinely accept a
//    margin of error of ±1 to 2 years", which rests on textbook assertion; the usual
//    authority is a 1982 Supreme Court judgment allowing two years either way. The item
//    now tests the defensible behaviour — report the method's margin of error — and card
//    E3 teaches that rather than the court-practice claim. Restore the original only if
//    the expert panel confirms the legal position.
//  • KQ5 options reordered so the correct answers spread a/b/c/d more evenly.
//  • Vignettes renumbered V1–V3. The generated reserve vignette (third molars at stage G
//    in an undocumented youth) and the three reserve knowledge items (Demirjian's seven
//    teeth, Lamendin's two criteria, aspartic acid racemisation) are held for validation.
//  • KQ2 and card E1 corrected to "28 weeks in utero to 23 years" — the generated draft
//    said "30 weeks … 23.5 years"; AlQahtani et al. 2010 (PMID 20310064) states 28 weeks.
//  • No item is keyed to a statutory section number: the Juvenile Justice Act 2015 and
//    BNS 2023 are not among the sources, so only the documentary-precedence principle is
//    tested (KQ11, V3).
import { baseDemographics, TF, mc, same, NOT_YET_SEEING } from './common';

const demographics = [
  ...baseDemographics,
  { key: 'A4', field: 'prior_training', type: 'single', required: true,
    stem: 'Have you received any formal teaching on dental age estimation so far?',
    options: same('Yes', 'No', 'Not sure') },
  { key: 'A5', field: 'workshop', type: 'single', required: true,
    stem: 'Have you attended any workshop, seminar or CDE on forensic odontology?',
    options: same('Yes', 'No') },
  { key: 'A6', field: 'info_source', type: 'single', required: true,
    stem: 'Your main source of information on dental age estimation',
    options: same('Lectures/curriculum', 'Textbooks/guidelines', 'Seniors/peers', 'Internet/apps', 'Other') },
];

// 12 items: 5 true/false (2 True, 3 False) and 7 MCQs keyed a, a, b, b, c, d, d.
const knowledge = [
  { key: 'KQ1', type: 'single', options: TF,
    stem: 'A dental age estimate is reported as an age range or probability rather than a single exact age.' },
  { key: 'KQ2', type: 'single', stem: 'Which atlas of tooth development is built on a modern sample covering 28 weeks in utero to 23 years?',
    options: mc('The Schour and Massler atlas', 'The London Atlas of Human Tooth Development', 'The Logan and Kronfeld atlas', 'The Todd skeletal atlas') },
  { key: 'KQ3', type: 'single', options: TF,
    stem: 'Emergence of a tooth through the gum is a more reliable indicator of age than its degree of mineralisation on a radiograph.' },
  { key: 'KQ4', type: 'single', stem: 'What is the main practical advantage of the Willems modification over the original Demirjian method?',
    options: mc('It gives maturity scores directly in years and reduces over-estimation', 'It replaces panoramic films with periapical films',
      'It applies only to third molar root completion', 'It removes the need to assess mandibular teeth') },
  { key: 'KQ5', type: 'single', stem: 'What does the Cameriere open-apex method measure on a radiograph of a child?',
    options: mc('Pulp chamber height and crown length of the upper central incisors', 'Intercanine arch width and mesiodistal crown width',
      'Root translucency length and periodontal attachment loss', 'Open root apices, normalised by tooth length, and the number of teeth with closed apices') },
  { key: 'KQ6', type: 'single', options: TF,
    stem: 'A third molar maturity index (I3M) above 0.08 indicates that the person has reached 18 years of age.' },
  { key: 'KQ7', type: 'single', stem: 'Which age-related change in an adult tooth correlates most strongly with age and is unaffected by restorative treatment?',
    options: mc('Occlusal attrition', 'Secondary dentine apposition', 'Cementum apposition', 'Root dentine transparency') },
  { key: 'KQ8', type: 'single', options: TF,
    stem: 'The original 1950 Gustafson formula remains the statistical standard for estimating age in adults.' },
  { key: 'KQ9', type: 'single', stem: 'Which method estimates adult age from pulp-to-tooth ratios measured on a periapical radiograph?',
    options: mc('The Gustafson method', 'The Lamendin method', 'The Kvaal method', 'The Bang and Ramm method') },
  { key: 'KQ10', type: 'single', options: TF,
    stem: 'An age estimate given to a court should state the margin of error of the method used.' },
  { key: 'KQ11', type: 'single', stem: 'In an Indian age dispute, how do documentary proof and dental age estimation rank?',
    options: mc('Dental estimation takes precedence over any document', 'A valid document takes precedence; dental estimation is used only when none exists',
      'A birth certificate and a dental estimate carry equal weight', 'Dental estimation is needed whenever the police request it') },
  { key: 'KQ12', type: 'single', stem: 'What combination does the AGFAD guidance recommend for estimating the age of a living adolescent?',
    options: mc('Physical examination, hand-wrist or clavicle radiography, and dental examination', 'A dental radiograph alone',
      'An intraoral examination with photographs', 'Dentine racemisation with a bone biopsy') },
];

// Attitude — 8 items. Reverse-coded (negatively worded): AQ2, AQ4, AQ5 (handled server-side).
const attitude = [
  { key: 'AQ1', type: 'likert',
    stem: 'Estimating age from teeth is a competency every dental graduate in India should have.' },
  { key: 'AQ2', type: 'likert',
    stem: 'Staging teeth on a radiograph is too complicated and slow to be of practical use.' },
  { key: 'AQ3', type: 'likert',
    stem: 'Dental undergraduates should be trained in staging developing teeth and applying age estimation methods.' },
  { key: 'AQ4', type: 'likert',
    stem: 'Estimating a person’s age without first checking for a valid birth record is acceptable practice.' },
  { key: 'AQ5', type: 'likert',
    stem: 'Age estimation concerns postgraduates only and has no place in undergraduate dental training.' },
  { key: 'AQ6', type: 'likert',
    stem: 'Dental age estimation protects the rights of children and juveniles in legal proceedings.' },
  { key: 'AQ7', type: 'likert',
    stem: 'Dentists should keep records and radiographs of a quality that would support a later age assessment.' },
  { key: 'AQ8', type: 'likert',
    stem: 'Combining dental, skeletal and physical findings gives a more reliable estimate than teeth alone.' },
];

// Self-efficacy / confidence — 5 items, one per core competency. Summed (max 25),
// no reverse-coding; reported separately from attitude.
const selfEfficacy = [
  { key: 'SE1', type: 'likert',
    stem: 'I feel confident staging developing teeth on a panoramic radiograph.' },
  { key: 'SE2', type: 'likert',
    stem: 'I feel confident choosing a suitable age estimation method for a child.' },
  { key: 'SE3', type: 'likert',
    stem: 'I feel confident judging from third molar development whether someone is likely to be 18.' },
  { key: 'SE4', type: 'likert',
    stem: 'I feel confident describing the methods used to estimate age in adults.' },
  { key: 'SE5', type: 'likert',
    stem: 'I feel confident explaining how an age estimate and its margin of error should be reported.' },
];

// Practice, in three parts:
//  • PQ1–PQ7 — self-reported frequency (descriptive, NOT scored).
//  • V1–V3   — applied vignettes, one defensible answer each, scored (max 3).
//  • IN1–IN2 — behavioural intention (descriptive Likert).
const practice = [
  { key: 'PQ1', type: 'single',
    stem: 'How often do you look at radiographs to judge how far a patient’s teeth have developed?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ2', type: 'single',
    stem: 'Have you ever applied a named age estimation method (Demirjian, Willems, Nolla) to a radiograph?',
    options: [...same('Yes', 'No', 'Not sure'), NOT_YET_SEEING] },
  { key: 'PQ3', type: 'single',
    stem: 'How often do you note third molar development during a routine examination?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ4', type: 'single',
    stem: 'Have you attended a lecture, workshop or CDE programme on dental age estimation?',
    options: same('Yes', 'No', 'Not sure') },
  { key: 'PQ5', type: 'single',
    stem: 'How often do you check a patient’s recorded date of birth against their dental development?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NOT_YET_SEEING] },
  { key: 'PQ6', type: 'single',
    stem: 'Have you ever recorded attrition or gum recession specifically to judge an adult’s age?',
    options: [...same('Yes', 'No', 'Not sure'), NOT_YET_SEEING] },
  { key: 'PQ7', type: 'single',
    stem: 'How often do you record written consent before taking a radiograph that is not needed for treatment?',
    options: [...same('Always', 'Often', 'Sometimes', 'Rarely', 'Never'), NOT_YET_SEEING] },
  {
    key: 'V1',
    type: 'single',
    stem: 'The age of an 8-year-old is disputed in court. A panoramic radiograph shows developing permanent teeth with open apices. The method to use is:',
    options: same(
      'Apply Willems’ or Cameriere’s open-apex method, designed for developing dentition.',
      'Apply Kvaal’s pulp-to-tooth ratios, which measure secondary dentine.',
      'Apply Gustafson’s six criteria to ground sections of an erupted tooth.',
      'Apply Lamendin’s method, using periodontal recession and root transparency.',
    ),
  },
  {
    key: 'V2',
    type: 'single',
    stem: 'Skeletonised adult remains are recovered with several sound single-rooted teeth. The police ask for an age estimate to narrow the missing-person search. The method to use is:',
    options: same(
      'Apply Lamendin’s method or Kvaal’s pulp-to-tooth ratio method to these teeth.',
      'Apply Demirjian’s eight-stage maturity scoring.',
      'Apply the Schour and Massler atlas of tooth development.',
      'Apply Cameriere’s open-apex formula for children.',
    ),
  },
  {
    key: 'V3',
    type: 'single',
    stem: 'A juvenile’s age is questioned in court. A verified school certificate giving the date of birth is on record, but the investigating officer asks for a dental age estimate. The correct course is:',
    options: same(
      'Accept the verified certificate as primary proof; a dental estimate is not required.',
      'Carry out the dental estimate, since a radiograph outranks a written certificate.',
      'Carry out both and report the average of the documented and dental ages.',
      'Request a histological estimate to confirm the date on the certificate.',
    ),
  },
  {
    key: 'IN1',
    type: 'likert',
    stem: 'When I begin clinical practice, I intend to keep radiographs and developmental findings on record for future reference.',
  },
  {
    key: 'IN2',
    type: 'likert',
    stem: 'I intend to attend continuing education on forensic age estimation when it is available to me.',
  },
];

const education = [
  {
    id: 'age-children',
    title: 'Estimating age in children',
    points: [
      'A dental age estimate is always a range or a probability, never a single exact age.',
      'How far a tooth has mineralised on a radiograph is a better guide than when it comes through the gum, which crowding and caries can delay.',
      'The London Atlas covers development from 28 weeks in utero to 23 years on a modern sample.',
      'For children, the Willems method gives maturity scores directly in years, and the Cameriere open-apex method measures the apices that are still open.',
    ],
    tip: 'Trust mineralisation over eruption — and report a range, not a date.',
  },
  {
    id: 'age-adults',
    title: 'Third molars, then adult teeth',
    points: [
      'A third molar maturity index (I3M) below 0.08 indicates that a person has probably reached 18; above it they are probably younger.',
      'Third molars are the most variable teeth, so they give probabilities, never a definite age.',
      'In adults, root dentine transparency tracks age most closely and is not disturbed by fillings; Gustafson’s original formula is historical, as its statistics were flawed.',
      'Kvaal’s pulp-to-tooth ratios work on an ordinary radiograph; Lamendin’s method and racemisation need the tooth itself.',
    ],
    tip: 'I3M under 0.08 points to 18 or over; in adults, look to root transparency.',
  },
  {
    id: 'age-reporting',
    title: 'What the court actually needs',
    points: [
      'A valid birth or school certificate takes precedence; a dental estimate is for cases where no such document exists.',
      'Report an age range with the margin of error of the method you used, and name the method and the reference population.',
      'A radiograph taken only for age estimation is not a diagnostic radiograph: consent and justification for the exposure must be recorded.',
    ],
    tip: 'Documents first, then teeth — and never a date without its margin of error.',
  },
  {
    id: 'age-combine',
    title: 'Teeth alone are not enough',
    points: [
      'AGFAD guidance for a living adolescent combines physical examination, a hand-wrist or clavicle radiograph, and a dental examination.',
      'Reference data are population-specific, so a formula built on one population may not fit the person in front of you.',
      'Good routine records — charts and radiographs — are what make a later age assessment or identification possible.',
    ],
    tip: 'Teeth, bones and body together; and check whose population the formula came from.',
  },
];

export default {
  slug: 'age',
  code: 'KS06',
  title: 'Effect of a Structured Educational Intervention on the Knowledge, Attitude, and Practice of Dental Age Estimation Among Dental Students and Interns: A Pre–Post Interventional Study',
  shortTitle: 'Dental Age Estimation',
  studyTitle: 'KAP on Dental Age Estimation',
  topic: 'Forensic odontology',
  emoji: '⏳',
  accent: '#F472B6',
  estMinutes: 12,
  mode: 'pre-post',
  status: 'live',
  version: '1.0',
  practiceMax: 3, // scored practice = the three applied vignettes (V1–V3); PQ*/IN* are descriptive
  blurb: 'Estimating age from teeth — which method fits a child, an adolescent or an adult, and how to report the answer.',
  sections: { demographics, knowledge, attitude, selfEfficacy, practice },
  education,
  educationTitle: 'Estimating age from teeth',
  educationBlurb: 'A two-minute refresher on the methods used at each stage of life, and on what a court needs from a report. Read through, then continue to the short follow-up.',
};
